import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { roleApi } from '@/services/permissionsRolesApi';
import { useToast } from '@/hooks/useToast';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, UserCog, BarChart3 } from 'lucide-react';
import type { RoleStatistics } from '@/types/permissionsRoles';

export const RoleStatsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<RoleStatistics | null>(null);
  const { hasRole } = useAdminAuth();
  const { toast } = useToast();

  const canAccess = hasRole('ADMIN') || hasRole('SYSTEM');

  useEffect(() => {
    if (canAccess) {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchStats is stable, run when canAccess changes
  }, [canAccess]);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const data = await roleApi.getStatistics();
      setStats(data);
    } catch (error: unknown) {
      console.error('Error fetching stats:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to load statistics',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">You do not have permission to view statistics.</p>
        <Button asChild className="mt-4">
          <Link to="/admin/roles">Back to List</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/roles">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="h-8 w-8" />
              Role Statistics
            </h1>
            <p className="text-muted-foreground mt-2">Overview of roles</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading statistics...</div>
      ) : !stats ? (
        <div className="text-center py-8 text-muted-foreground">No statistics available</div>
      ) : (
        <>
          {/* Overview Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Roles</CardTitle>
                <UserCog className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground">All roles</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active</CardTitle>
                <UserCog className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                <p className="text-xs text-muted-foreground">Currently active</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Inactive</CardTitle>
                <UserCog className="h-4 w-4 text-gray-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-600">{stats.inactive}</div>
                <p className="text-xs text-muted-foreground">Currently inactive</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">System</CardTitle>
                <UserCog className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.system}</div>
                <p className="text-xs text-muted-foreground">System roles</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Custom</CardTitle>
                <UserCog className="h-4 w-4 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">{stats.custom}</div>
                <p className="text-xs text-muted-foreground">Custom roles</p>
              </CardContent>
            </Card>
          </div>

          {/* Permission Distribution */}
          {Object.keys(stats.permissionDistribution).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Permission Distribution</CardTitle>
                <CardDescription>Number of roles by permission count</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(stats.permissionDistribution)
                    .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
                    .map(([count, roleCount]) => (
                      <div key={count} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{count} permission(s)</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(roleCount / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{roleCount}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Roles by Permission Count */}
          {stats.topRolesByPermissionCount && stats.topRolesByPermissionCount.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Roles by Permission Count</CardTitle>
                <CardDescription>Roles with the most permissions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.topRolesByPermissionCount.map((role, index) => (
                    <div key={role.roleId} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">#{index + 1}</span>
                        <span className="font-medium">{role.roleName}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-32 bg-secondary rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{
                              width: `${(role.permissionCount / (stats.topRolesByPermissionCount?.[0]?.permissionCount || 1)) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium w-12 text-right">
                          {role.permissionCount}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Activity */}
          {stats.recentActivity && stats.recentActivity.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Last 10 created/updated roles</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stats.recentActivity.map((role) => (
                    <div
                      key={role.id}
                      className="flex items-center justify-between p-3 rounded-md border"
                    >
                      <div>
                        <div className="font-medium">{role.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(role.updatedAt).toLocaleString()}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/admin/roles/${role.id}`}>View</Link>
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default RoleStatsPage;
